import * as React from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container, Box, Grid, Paper, ButtonBase, Typography, Badge, Alert, IconButton,
  useMediaQuery, useTheme,
} from "@mui/material";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { MenuInicial } from "./MenuInicial";
import axios from "axios";
import {
  API_BASE_URL, VERIFICARVENCIMIENTO, TURNO_ACTUAL, TURNO_GET, ANALISIS_GET,
} from '../services/apiConfig';
import PagoLicenciaForm from './PagoLicenciaForm';
import { MenuItemsConfig } from '../config/menuItems.jsx';
import gridoIcon from '../images/grido.jpg';
import { avisar } from '../services/toast';

const authHeader = () => {
  const t = sessionStorage.getItem('authToken');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmtMoneda = (n) => `$${Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// "Hoy" según el reloj de Argentina, NO el del navegador ni el de PHP en el
// servidor (que puede estar en otro huso horario) — la base guarda las
// ventas en hora Argentina (-03:00, fijo, sin horario de verano), así que
// "hoy" tiene que calcularse igual para que coincida con esos datos.
const hoyArgentina = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Argentina/Buenos_Aires' }).format(new Date());

// Interpreta un datetime "YYYY-MM-DD HH:mm:ss" de la base (guardado con
// NOW() de MySQL bajo time_zone='-03:00') como hora de Argentina — Argentina
// no tiene horario de verano, así que el offset fijo -03:00 siempre es correcto.
const argentinaMs = (mysqlDatetime) => new Date(mysqlDatetime.replace(' ', 'T') + '-03:00').getTime();

function Dashboard() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const userData = JSON.parse(sessionStorage.getItem("userData"));
  const nombreUsuario = userData?.username || userData?.nombre || "Usuario";
  const perfil = Number(userData?.perfil);
  const sucursalId = userData?.sucursalid;
  // sucursalid=999 es el centinela de "sin sucursal única" que ya usa
  // MenuInicial — con eso no tiene sentido pedir el turno actual.
  const tieneSucursalPropia = !!sucursalId && Number(sucursalId) !== 999;

  const [horaActual, setHoraActual] = useState(new Date());
  const [dbName, setDbName] = useState('');
  const [licencia, setLicencia] = useState(null);
  // undefined = todavía no se sabe, null = cerrado, objeto = abierto.
  const [turnoActual, setTurnoActual] = useState(undefined);
  // null = todavía no se sabe; [] = se sabe y no hubo ventas hoy; array = ventas por sucursal.
  const [porSucursalHoy, setPorSucursalHoy] = useState(null);
  const [avisoTurnoCerrado, setAvisoTurnoCerrado] = useState(false);

  const colorSistema = localStorage.getItem('color_sistema') || '#071c44';
  const iconColor = localStorage.getItem('color_icono') || '#a9b9a6';

  useEffect(() => {
    const timer = setInterval(() => setHoraActual(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    axios.get(API_BASE_URL + VERIFICARVENCIMIENTO, { headers: authHeader() })
      .then((res) => {
        if (res.data?.data) {
          setDbName(res.data.data.db_name || '');
          setLicencia(res.data.data);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (perfil !== 3 || !tieneSucursalPropia) return;
    axios.get(API_BASE_URL + TURNO_ACTUAL, { params: { sucursal_id: sucursalId }, headers: authHeader() })
      .then((res) => {
        const turno = res.data?.data ?? null;
        setTurnoActual(turno);
        if (turno?.id) {
          axios.get(API_BASE_URL + TURNO_GET, { params: { id: turno.id }, headers: authHeader() })
            .then((res2) => setTurnoActual((prev) => ({ ...prev, ...res2.data?.data })))
            .catch(() => {});
        }
      })
      .catch(() => setTurnoActual(null));
  }, [perfil, sucursalId, tieneSucursalPropia]);

  useEffect(() => {
    if (perfil !== 1 && perfil !== 2) return;
    const hoy = hoyArgentina();
    axios.get(API_BASE_URL + ANALISIS_GET, { params: { startDate: hoy, endDate: hoy }, headers: authHeader() })
      .then((res) => setPorSucursalHoy(res.data?.data?.por_sucursal ?? []))
      .catch(() => setPorSucursalHoy([]));
  }, [perfil]);

  const accesos = (MenuItemsConfig[perfil] || MenuItemsConfig[3]).filter((item) => item.title !== 'Inicio');
  const licenciaEnAlerta = licencia && (licencia.expirado || (licencia.dias_restantes !== null && licencia.dias_restantes <= 7));

  // Turno abierto hace más de 12hs: a partir de ahí registroalta.php ya
  // bloquea nuevas ventas, así que este cartel avisa ANTES de que alguien
  // intente vender y se encuentre con el bloqueo sin entender por qué. Se
  // avisa una sola vez por turno/sesión (botón "Entendido" abajo).
  const horasTurnoAbierto = turnoActual?.fecha_apertura ? (Date.now() - argentinaMs(turnoActual.fecha_apertura)) / 3600000 : 0;
  const turnoViejo = perfil === 3 && !!turnoActual && horasTurnoAbierto > 12;
  const claveAviso = `turno_viejo_visto_${turnoActual?.id}`;
  const mostrarAvisoTurno = turnoViejo && !avisoTurnoCerrado && sessionStorage.getItem(claveAviso) !== '1';

  return (
    <Container maxWidth={isMobile ? false : "xl"} sx={{ px: isMobile ? 1 : 3 }}>
      <MenuInicial />

      <Box sx={{ my: 3 }}>
        {/* Encabezado */}
        <Paper
          sx={{
            p: isMobile ? 2 : 3, mb: 3, display: 'flex', alignItems: 'center', gap: 2,
            background: `linear-gradient(135deg, ${colorSistema} 0%, ${colorSistema}dd 100%)`,
            color: 'white', borderRadius: 2,
          }}
        >
          <Box component="img" src={gridoIcon} alt="Grido" sx={{ height: isMobile ? 40 : 52, borderRadius: 1.5, flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 700 }}>
              ¡Hola, {nombreUsuario}!
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {horaActual.toLocaleDateString('es-AR', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' })}
              {' · '}
              {horaActual.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false })}
            </Typography>
          </Box>
        </Paper>

        {licenciaEnAlerta && (
          <Alert severity={licencia.expirado ? 'error' : 'warning'} sx={{ mb: 3 }}>
            {licencia.expirado
              ? 'La licencia venció.'
              : `La licencia vence en ${licencia.dias_restantes} día${licencia.dias_restantes === 1 ? '' : 's'}.`}
          </Alert>
        )}

        {mostrarAvisoTurno && (
          <Alert
            severity="warning"
            sx={{ mb: 3 }}
            action={
              <Box sx={{ display: 'flex', gap: 1 }}>
                <ButtonBase
                  onClick={() => navigate('/turnos')}
                  sx={{ px: 1.5, py: 0.5, borderRadius: 1, fontWeight: 700, fontSize: '0.8125rem', color: 'warning.dark' }}
                >
                  Ir a cerrarlo
                </ButtonBase>
                <ButtonBase
                  onClick={() => { sessionStorage.setItem(claveAviso, '1'); setAvisoTurnoCerrado(true); }}
                  sx={{ px: 1.5, py: 0.5, borderRadius: 1, fontSize: '0.8125rem', color: 'text.secondary' }}
                >
                  Entendido
                </ButtonBase>
              </Box>
            }
          >
            Este turno lleva abierto <strong>{Math.floor(horasTurnoAbierto)} horas</strong> (desde el {turnoActual.fecha_apertura}).
            Cerralo pronto — a partir de las 12hs el sistema no va a dejar registrar ventas nuevas hasta que lo cierres.
          </Alert>
        )}

        {(perfil === 1 || perfil === 2) && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 700 }}>
              Ventas de hoy {perfil === 2 ? 'por sucursal' : ''}
            </Typography>
            {porSucursalHoy === null ? (
              <Typography variant="body2" color="text.secondary">Cargando...</Typography>
            ) : porSucursalHoy.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 2 }}>Todavía no hay ventas registradas hoy.</Alert>
            ) : (
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                {porSucursalHoy.map((s) => (
                  <Paper key={s.sucursal_id} variant="outlined" sx={{ px: 2.5, py: 1.5, flex: '1 1 180px', borderRadius: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: colorSistema, lineHeight: 1.2 }}>
                      {fmtMoneda(s.importe)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {s.cantidad_registros} venta{Number(s.cantidad_registros) === 1 ? '' : 's'}
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>{s.sucursal_nombre}</Typography>
                  </Paper>
                ))}
                {porSucursalHoy.length > 1 && (
                  <Paper sx={{ px: 2.5, py: 1.5, flex: '1 1 180px', borderRadius: 2, bgcolor: colorSistema, color: 'white' }}>
                    <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                      {fmtMoneda(porSucursalHoy.reduce((acc, s) => acc + Number(s.importe || 0), 0))}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', opacity: 0.9 }}>
                      {porSucursalHoy.reduce((acc, s) => acc + Number(s.cantidad_registros || 0), 0)} ventas
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>Total ({porSucursalHoy.length} sucursales)</Typography>
                  </Paper>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Grilla de accesos directos */}
        <Grid container spacing={isMobile ? 1.5 : 2}>
          {accesos.map((item) => {
            const esTurnos = item.title === 'Turnos de caja';
            const esRegistrar = item.title === 'Registrar';
            const mostrarPuntoTurno = perfil === 3 && esTurnos && turnoActual !== undefined;
            return (
              <Grid item xs={4} sm={3} md={2} key={item.route}>
                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                  <ButtonBase
                    onClick={() => navigate(item.route)}
                    sx={{
                      width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center',
                      gap: 0.75, p: isMobile ? 1.25 : 1.75, transition: 'transform .15s, background-color .15s',
                      '&:hover': { transform: 'translateY(-3px)', bgcolor: 'action.hover' },
                    }}
                  >
                    <Badge
                      color={turnoActual ? 'success' : 'default'}
                      variant="dot"
                      invisible={!mostrarPuntoTurno}
                    >
                      <Box
                        sx={{
                          width: isMobile ? 46 : 56, height: isMobile ? 46 : 56, borderRadius: '50%',
                          bgcolor: colorSistema, color: iconColor, display: 'flex',
                          alignItems: 'center', justifyContent: 'center',
                          '& svg': { fontSize: isMobile ? 22 : 26 },
                        }}
                      >
                        {item.icon}
                      </Box>
                    </Badge>
                    <Typography variant="caption" sx={{ textAlign: 'center', fontWeight: 600, lineHeight: 1.25 }}>
                      {item.title}
                    </Typography>
                    {perfil === 3 && esRegistrar && turnoActual?.ventas_total !== undefined && (
                      <Typography variant="caption" color="text.secondary">
                        {fmtMoneda(turnoActual.ventas_total)}
                      </Typography>
                    )}
                  </ButtonBase>
                </Paper>
              </Grid>
            );
          })}
        </Grid>

        {Number(perfil) === 2 && (
          <Box sx={{ mt: 3 }}>
            <PagoLicenciaForm />
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
          <Paper
            variant="outlined"
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.75,
              px: 1.25, py: 0.5, borderRadius: 1.5, bgcolor: 'grey.50',
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {API_BASE_URL}{dbName && ` · DB: ${dbName}`}
            </Typography>
            <IconButton
              size="small"
              sx={{ p: 0.25 }}
              onClick={() => {
                navigator.clipboard.writeText(`${API_BASE_URL}${dbName ? ` · DB: ${dbName}` : ''}`)
                  .then(() => avisar('Copiado al portapapeles', 'info'))
                  .catch(() => avisar('No se pudo copiar al portapapeles', 'error'));
              }}
            >
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Paper>
        </Box>
      </Box>
    </Container>
  );
}

export default Dashboard;
